import { useState } from 'react'

import { track, useTrackOnMount } from '@onl-web/shared/analytics'
import { useBridge } from '@onl-web/shared/bridge'

import clover from '@/assets/icons/clover.png'
import type { GachaDrawResponse } from '@/types/gacha'

import ConfettiLottie from './ConfettiLottie'
import {
  ActionButton,
  BottomArea,
  ButtonArea,
  CloseButton,
  CloverIcon,
  Container,
  ContentArea,
  DescriptionText,
  FooterText,
  MinorDescriptionText,
  MinorPriceText,
  PriceText,
  ProgressBarArea,
  ProgressBarFill,
  ProgressBarTrack,
  ProgressCard,
  ProgressGreenText,
  ProgressHeader,
  ProgressLabelCurrent,
  ProgressLabelEnd,
  ProgressLabels,
  ProgressTextArea,
  TextArea,
} from './styles'

interface ResultStepProps {
  drawResult: GachaDrawResponse
  onRetry: () => void
  onError?: (title: string, description?: string) => void
}

const NOUNS = [
  '대청소',
  '냉장고 청소',
  '쓰레기처리',
  '짐정리',
  '헌옷 정리',
  '자취방 청소',
  '묵은 짐정리',
  '재활용 처리',
  '소형가전 처리',
  '이사짐 정리',
] as const

const MINOR_REWARD_TIER = 6

export default function ResultStep({ drawResult, onRetry, onError }: ResultStepProps) {
  const [noun] = useState(() => NOUNS[Math.floor(Math.random() * NOUNS.length)])
  const { callHandler } = useBridge()

  useTrackOnMount('view_gacha_result', {})

  const handleOrder = async () => {
    track('click_gacha_result_btn', { btn_name: '수거 신청하기' })
    try {
      await callHandler('openBottom', { type: 'order' })
    } catch (err) {
      onError?.('수거 신청을 시작할 수 없어요', err instanceof Error ? err.message : undefined)
    }
  }

  const handleClose = () => {
    track('click_gacha_result_btn', { btn_name: '닫기' })
    onRetry()
  }

  const isMinor = drawResult.rewardTier === MINOR_REWARD_TIER
  const rewardTier = drawResult.rewardTier

  const pityRemaining = drawResult.pityGauge.threshold - drawResult.pityGauge.current + 1
  const pityText = pityRemaining === 1 ? '한번 더 찾으면' : `${pityRemaining}번 더 찾으면`
  const pityProgressPercent =
    drawResult.pityGauge.threshold > 0 ? (drawResult.pityGauge.current / drawResult.pityGauge.threshold) * 100 : 0

  return (
    <Container>
      <ContentArea>
        <ConfettiLottie rewardTier={rewardTier} isMinor={isMinor} />
        <TextArea>
          {isMinor ? (
            <>
              <MinorPriceText>{drawResult.rewardName} 발견!</MinorPriceText>
              <MinorDescriptionText>다음엔 더 큰 금액을 찾을 수 있을 거예요</MinorDescriptionText>
            </>
          ) : (
            <>
              <PriceText>{drawResult.rewardName} 발견!</PriceText>
              <DescriptionText>{`지금 바로 할인 받고\n${noun} 해요`}</DescriptionText>
            </>
          )}
        </TextArea>
      </ContentArea>

      {isMinor && (
        <ProgressCard>
          <ProgressHeader>
            <CloverIcon src={clover} alt='' loading='eager' decoding='async' />
            <ProgressTextArea>
              <ProgressGreenText>{pityText}</ProgressGreenText>
              <br />
              최소 700원 이상 쿠폰을 받을 수 있어요
            </ProgressTextArea>
          </ProgressHeader>
          <ProgressBarArea>
            <ProgressBarTrack>
              <ProgressBarFill percent={pityProgressPercent} />
            </ProgressBarTrack>
            <ProgressLabels>
              <ProgressLabelCurrent>지금 여기!</ProgressLabelCurrent>
              <ProgressLabelEnd>무조건 당첨</ProgressLabelEnd>
            </ProgressLabels>
          </ProgressBarArea>
        </ProgressCard>
      )}

      <BottomArea>
        {!isMinor && <FooterText>*쿠폰 유효기간은 14일입니다.</FooterText>}
        <ButtonArea>
          <CloseButton onClick={handleClose}>닫기</CloseButton>
          <ActionButton onClick={handleOrder}>수거 신청하기</ActionButton>
        </ButtonArea>
      </BottomArea>
    </Container>
  )
}
